import { IMoment, moment } from '@lib/moment';
import { PhxSelectOption } from 'phx-core/lib/select/select.types';

import { IKebabMenuOption } from '@app/common-components/kebab-menu.component';
import { IDetailCardContent } from '@app/common-components/detail-card.component';

import { DISPATCH_DATE_FORMATS, DISPATCH_STAGE_TYPE, getFormattedDateTime, STATUSES } from '../dispatch-utils';

import { IDispatch, IServerDispatch } from '../dispatch.interfaces';
import { IPageHeaderTab } from '@misc/page-header/page-header.component';
import { IDateRange } from '@lib/state-manager';
import { DispatchContentI18n } from '@lib/i18n-backend-strings';
import { IResourcePlanDispatchCard } from './manage-dispatch.service';
import { IOption } from '@misc/filter/filters.service';

export const VIRTUAL_ITEMS_HEIGHT: number = 106;
export const VIRTUAL_ITEMS_MIN_BUFFER_SIZE_PX: number = 900;
export const VIRTUAL_ITEMS_MAX_BUFFER_SIZE_PX: number = 1200;
export const NZ_MESSAGE_DURATION: number = 5000;
export const NO_DATE_SET = $localize`NO DATE SET`;

export const DISPATCH_ENTITY_STATUSES = {
  moving: 'Driving',
  idling: 'Idle',
  off: 'Stationary',
  stale: 'Stale',
  default: 'Default',
};

export enum MAP_CONTROL_LABELS {
  LIVE_REDIRECT = 'View Live',
  TRIP_HISTORY = 'View Trip History',
}

export enum DISPATCH_MENU_ACTION_KEYS {
  CANCEL = 'cancel',
  VIEW_DETAILS = 'view-details',
  MARK_AS_COMPLETED = 'mark-as-completed',
  EDIT = 'edit',
  CREATE_DISPATCH = 'create-dispatch',
  DUPLICATE = 'duplicate',
  START_DISPATCH = 'start-dispatch',
  SEND_TO_DRIVER = 'send-to-driver',
  ASSIGN = 'assign',
}

export enum JOB_ACTION_KEYS {
  ASSIGN_JOB_TO_DRIVER = 'assign-job-to-driver',
  EDIT_JOB = 'edit-job',
  CANCEL_JOB = 'cancel-job',
}

export const MANAGE_DISPATCH_TABS: IManageDispatchTab[] = [
  {
    label: $localize`Plan`,
    routeLink: '/dispatch/manage/list/plan',
  },
  {
    label: $localize`In Progress`,
    routeLink: '/dispatch/manage/list/in-progress',
  },
  {
    label: $localize`Done`,
    routeLink: '/dispatch/manage/list/done',
  },
];

export const MESSAGE_ICONS_TOOLTIPS = {
  NEW_MESSAGE: $localize`new message`,
  NEW_MESSAGES: $localize`new messages`,
  NO_NEW_MESSAGES: $localize`No new messages`,
  SEND_MESSAGE: $localize`Send message`,
};

export const MESSAGE_ICONS = {
  NEW_MESSAGES_ICON: '/dispatch/new-message-icon.svg',
  NO_NEW_MESSAGES_ICON: '/dispatch/no-new-message-icon.svg',
  SEND_MESSAGE_ICON: '/dispatch/send-message-icon.svg',
};

export const IN_PROGRESS_DETAIL_OPTIONS: IKebabMenuOption[] = [
  { label: $localize`Edit`, key: DISPATCH_MENU_ACTION_KEYS.EDIT },
  { label: $localize`Cancel`, key: DISPATCH_MENU_ACTION_KEYS.CANCEL },
  { label: $localize`Duplicate`, key: DISPATCH_MENU_ACTION_KEYS.DUPLICATE },
];

export const DONE_DETAIL_OPTIONS: IKebabMenuOption[] = [
  { label: $localize`Edit`, key: DISPATCH_MENU_ACTION_KEYS.EDIT },
  { label: $localize`Duplicate`, key: DISPATCH_MENU_ACTION_KEYS.DUPLICATE },
];

export enum DISPATCH_STOP_TYPE {
  PICKUP = 'Pickup',
  DELIVERY = 'Delivery',
}

export enum DISPATCH_ACTIVITY_TYPES {
  SUBMITTED_FORM = 'submitted_form',
  UPDATED_ORDER = 'updated_order',
  EDITED_ORDER = 'edited_order',
  EDITED_DISPATCH = 'edited_dispatch',
  CREATED_ORDER = 'created_order',
  CREATED_DISPATCH = 'created_dispatch',
  STARTED_DISPATCH = 'started_dispatch',
  ARRIVED_AT = 'arrived_at',
  INVOICE_ORDER = 'invoice_order',
  CANCELED_DISPATCH = 'canceled_dispatch',
  PAID_ORDER = 'paid_order',
  COMPLETED = 'completed',
  DEPARTED = 'departed',
  SENT_TO_DRIVER = 'sent_to_driver',
  COMPLETED_DISPATCH = 'completed_dispatch',
}

export enum MANAGE_DISPATCH_FLASH_MESSAGES {
  INVALID_URL = 'Invalid URL',
  SHARE_URL_NOT_FOUND = 'Share URL not found',
  DISPATCH_NOT_FOUND = 'Dispatch not found',
}

export interface ITabRouteParams {
  sort_by?: string;
  dispatch_status?: string[];
  include_eta?: boolean;
  start_date?: string;
  end_date?: string;
  driver_ids?: string[];
}

export interface ITabStateParams {
  sort_by?: string;
  dispatch_status?: string[];
  include_eta?: boolean;
  date_range?: IDateRange;
  driver_ids?: string[];
}

export interface IResourcePlanSelectedIndices {
  resourceIndex: number;
  groupIndex?: number; // TODO: Remove in DDW-2599
  dispatchIndex: number;
}

export interface IResourcePlanSelectedDispatches {
  indices: IResourcePlanSelectedIndices[];
  dispatches: IResourcePlanDispatchCard[];
}

export enum GET_DISPATCHES_QUERY_PARAM_KEYS {
  STATUSES = 'statuses',
  INCLUDE_ETA = 'include_eta',
  SORT_BY = 'sort_by',
  DISPATCH_STATUS = 'dispatch_status',
  DRAFT_STATUS = 'draft_status',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  DRIVER_IDS = 'driver_ids',
  DRIVER_ASSIGNMENT_STATUS = 'driver_assignment_status',
  PLANNED_DATE = 'planned_date',
}

export enum GET_DISPATCHES_STATE_PARAM_KEYS {
  STATUSES = 'statuses',
  INCLUDE_ETA = 'include_eta',
  SORT_BY = 'sort_by',
  DISPATCH_STATUS = 'dispatch_status',
  DRAFT_STATUS = 'draft_status',
  DATE_RANGE = 'date_range',
  DRIVER_IDS = 'driver_ids',
  DRIVER_ASSIGNMENT_STATUS = 'driver_assignment_status',
  PLANNED_DATE = 'planned_date',
}

export enum GET_DISPATCH_BY_ID_PARAM_KEYS {
  INCLUDE_ETA = 'include_eta',
}

export enum SORT_OPTIONS {
  EARLIEST_FIRST_STOP = 'earliest_first_stop',
  OLDEST_STATUS_UPDATE = 'oldest_status_update',
  MOST_RECENT = 'most_recently_completed',
  MOST_REVENUE = 'most_order_revenue',
  LATEST_ETA = 'latest_eta',
  CLOSEST_TO_NEXT_STOP = 'closest_to_next_stop',
  CUSTOMER_NAME = 'customer_name',
}

export enum DRIVER_ASSIGNMENT_OPTIONS {
  DRIVERS_WITH_ASSIGNMENT = 'drivers_with_assignment',
  DRIVERS_WITHOUT_ASSIGNMENT = 'drivers_without_assignment',
}

export enum DRAFTS_STATUS_OPTIONS {
  DRAFTS = 'drafts',
  SENT_TO_DRIVER = 'sent_to_driver',
}

export enum DISPATCH_STATUS_PARAM_KEYS {
  ASSIGNED = 'assigned',
  UNASSIGNED = 'unassigned',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
  IN_TRANSIT = 'in_transit',
  AT_STOP = 'at_stop',
  AT_LAST_STOP = 'at_last_stop',
}

export enum API_ENDPOINTS {
  GET_DISPATCHES = 'dispatches',
  GET_ASSIGNED_DISPATCHES = 'dispatches/assigned',
  GET_UNASSIGNED_DISPATCHES = 'dispatches/unassigned',
  START_DISPATCH = 'start_dispatch',
  COMPLETE_DISPATCH = 'complete_dispatch',
  CANCEL_DISPATCHES = 'cancel_dispatches',
  ASSIGN_DISPATCHES = 'assign_dispatches',
  PUBLISH_DISPATCHES = 'publish_dispatches',
  UNASSIGN_DISPATCHES = 'unassign_dispatches',
}

export const GET_DISPATCHES_QUERY_PARAM_VALUES = {
  PAGE_SIZE: 1000,
  TAB_PAGE_SIZE: 25,
  DEFAULT_PAGE_NUMBER: 1,
  PLAN_TAB_STATUSES: [STATUSES.PLANNED, STATUSES.PENDING],
  IN_PROGRESS_TAB_STATUSES: [STATUSES.ACTIVE],
  DONE_TAB_STATUSES: [STATUSES.CANCELLED, STATUSES.COMPLETED],
  INCLUDE_ETA: true,
};

export const DISPATCH_TABLE_SCROLL_THRESHOLD: number = 500;

export const PLAN_TAB_SORT_OPTIONS: Array<PhxSelectOption & IOption> = [
  { label: $localize`Earliest first stop`, value: SORT_OPTIONS.EARLIEST_FIRST_STOP, key: SORT_OPTIONS.EARLIEST_FIRST_STOP },
  { label: $localize`Oldest status update`, value: SORT_OPTIONS.OLDEST_STATUS_UPDATE, key: SORT_OPTIONS.OLDEST_STATUS_UPDATE },
];

export const PLAN_TAB_DRIVER_STATUS_OPTIONS: Array<PhxSelectOption & IOption> = [
  {
    label: $localize`Drivers with assignment`,
    key: DRIVER_ASSIGNMENT_OPTIONS.DRIVERS_WITH_ASSIGNMENT,
    value: DRIVER_ASSIGNMENT_OPTIONS.DRIVERS_WITH_ASSIGNMENT,
  },
  {
    label: $localize`Drivers without assignment`,
    key: DRIVER_ASSIGNMENT_OPTIONS.DRIVERS_WITHOUT_ASSIGNMENT,
    value: DRIVER_ASSIGNMENT_OPTIONS.DRIVERS_WITHOUT_ASSIGNMENT,
  },
];

export const PLAN_TAB_DRAFT_STATUS_OPTIONS: Array<PhxSelectOption & IOption> = [
  { label: $localize`Drafts`, key: DRAFTS_STATUS_OPTIONS.DRAFTS, value: DRAFTS_STATUS_OPTIONS.DRAFTS },
  { label: $localize`Sent to drivers`, key: DRAFTS_STATUS_OPTIONS.SENT_TO_DRIVER, value: DRAFTS_STATUS_OPTIONS.SENT_TO_DRIVER },
];

export const DONE_TAB_SORT_OPTIONS: Array<IOption & PhxSelectOption> = [
  { label: $localize`Most recent`, key: SORT_OPTIONS.MOST_RECENT, value: SORT_OPTIONS.MOST_RECENT },
  { label: $localize`Most revenue`, key: SORT_OPTIONS.MOST_REVENUE, value: SORT_OPTIONS.MOST_REVENUE },
];

export const IN_PROGRESS_SORT_OPTIONS: Array<IOption & PhxSelectOption> = [
  { label: $localize`Latest ETA`, key: SORT_OPTIONS.LATEST_ETA, value: SORT_OPTIONS.LATEST_ETA },
  { label: $localize`Closest to next stop`, key: SORT_OPTIONS.CLOSEST_TO_NEXT_STOP, value: SORT_OPTIONS.CLOSEST_TO_NEXT_STOP },
  { label: $localize`Customer name`, key: SORT_OPTIONS.CUSTOMER_NAME, value: SORT_OPTIONS.CUSTOMER_NAME },
];

export interface IManageDispatchTab {
  label: string;
  routeLink: string;
}

export interface IDispatchDetailCard {
  header: string;
  content: IDetailCardContent[];
}

function getFormattedTimeWindowString(
  startDateTime: string,
  endDateTime: string,
  endTime: string,
  startDate: string,
  endDate: string,
): string {
  if (startDateTime === endDateTime) return startDateTime;
  if (startDate === endDate) {
    return startDateTime && endTime ? `${startDateTime} – ${endTime}` : startDateTime || endDateTime;
  }
  return startDateTime && endDateTime ? `${startDateTime} – ${endDateTime}` : startDateTime || endDateTime;
}

export function getTableScrollHeight(filtersHeight: number, tableHeaderHeight: number): string {
  return `calc(100% - ${filtersHeight ? filtersHeight - tableHeaderHeight : tableHeaderHeight}px)`;
}

export function getDispatchStatus(dispatch: IDispatch, stage: string): string {
  if (stage === DISPATCH_STAGE_TYPE.IN_PROGRESS) return dispatch?.inProgressStatus;
  else if (stage === DISPATCH_STAGE_TYPE.DONE) return dispatch?.state;
}

export function getFormattedTimeWindow(startDatetime: string, endDatetime: string, timezone: string): string {
  const { TIME, DATETIME, DATE } = DISPATCH_DATE_FORMATS;
  const formattedStartDatetime: string = startDatetime && getFormattedDateTime(
    startDatetime, DATETIME, timezone, true,
  );
  const formattedEndDatetime: string = endDatetime && getFormattedDateTime(
    endDatetime, DATETIME, timezone, true,
  );
  const formattedStartDate: string = startDatetime && getFormattedDateTime(
    startDatetime, DATE, timezone, true,
  );
  const formattedEndDate: string = endDatetime && getFormattedDateTime(
    endDatetime, DATE, timezone, true,
  );
  const formattedEndTime: string = endDatetime && getFormattedDateTime(
    endDatetime, TIME, timezone, true,
  );

  return getFormattedTimeWindowString(
    formattedStartDatetime,
    formattedEndDatetime,
    formattedEndTime,
    formattedStartDate,
    formattedEndDate,
  );
}

export function getFormattedTime(startDatetime: string, endDatetime: string, timezone: string): string {
  const { TIME_FORMAT, DATE_FORMAT, DATE } = DISPATCH_DATE_FORMATS;
  const formattedStartDatetime: string = startDatetime && getFormattedDateTime(
    startDatetime, DATE_FORMAT, timezone, false,
  );
  const formattedEndDatetime: string = endDatetime && getFormattedDateTime(
    endDatetime, DATE_FORMAT, timezone, false,
  );
  const formattedStartDate: string = startDatetime && getFormattedDateTime(
    startDatetime, DATE, timezone, true,
  );
  const formattedEndDate: string = endDatetime && getFormattedDateTime(
    endDatetime, DATE, timezone, true,
  );
  const formattedEndTime: string = endDatetime && getFormattedDateTime(
    endDatetime, TIME_FORMAT, timezone, false,
  );

  return getFormattedTimeWindowString(
    formattedStartDatetime,
    formattedEndDatetime,
    formattedEndTime,
    formattedStartDate,
    formattedEndDate,
  );
}

export function isSameDay(time: string): boolean {
  return moment(time).isSame(moment(), 'day');
}

export function getDispatchTabs(): IPageHeaderTab[] {
  return [
    {
      label: $localize`Plan`,
      link: '/dispatch/manage/list/plan',
      value: '/dispatch/manage/list/plan',
      shouldNavItemShow: true,
      isNew: false,
    },
    {
      label: $localize`In Progress`,
      link: '/dispatch/manage/list/in-progress',
      value: 'in-progress',
      shouldNavItemShow: true,
      isNew: false,
    },
    {
      label: $localize`Done`,
      link: '/dispatch/manage/list/done',
      value: '/dispatch/manage/list/done',
      shouldNavItemShow: true,
      isNew: false,
    },
  ];
}

export function getDispatchCardTitle(
  dispatch: IServerDispatch,
  isDispatchRoute: boolean,
): string {
  const { order_itineraries, order, dispatch_route } = dispatch;
  const stopCount: number = order_itineraries.length;
  const firstStopLocationName: string = order_itineraries[0]?.dispatch_location.name;
  const lastStopLocationName: string = order_itineraries[stopCount - 1]?.dispatch_location.name;

  const multiStopPartialTitle: string = `${firstStopLocationName} - ${lastStopLocationName}`;
  const singleStopPartialTitle: string = firstStopLocationName;

  if (order_itineraries.length > 1) {
    if (isDispatchRoute && !!order?.order_number) return `${order.order_number} : ${multiStopPartialTitle}`;
    else if (isDispatchRoute) return multiStopPartialTitle;
    else return order?.order_number;
  } else {
    if (isDispatchRoute && !!order?.order_number) return `${order.order_number} : ${singleStopPartialTitle}`;
    else if (isDispatchRoute) return singleStopPartialTitle;
    else return order?.order_number;
  }
}

export function getStartOfDay(date: IMoment): string {
  if (date && date.isValid()) return date.startOf('day').toISOString();
  return null;
}

export function getEndOfDay(date: IMoment): string {
  if (date && date.isValid()) return date.endOf('day').toISOString();
  return null;
}

export function getChangeNameI18n(changeName: string): string {
  const allI18nKeys: string[] = Object.keys(DispatchContentI18n);

  // Check if changeName starts with any of the keys in DispatchContentI18n
  // assuming that the location is always at the end of the string
  // get the translated string and then append the location
  for (const key of allI18nKeys) {
    if (changeName.startsWith(key) && changeName !== key ) {
      return `${DispatchContentI18n[key]} ${changeName.split(key)[1]}`;
    }
  }

  // If the changeName doesn't have a location at the end
  // return the translated string
  return DispatchContentI18n[changeName];
}